/**
 * Model delta — partial code change → partial data change.
 *
 * `diffModel` compares two flattened board inputs (`{ modules, edges }`, the
 * same shape `buildBoardFromModules` / `createBoardEngine` consume) and reports
 * what changed PLUS the set of space ids whose layout is now stale. Feed
 * `delta.dirtySpaces` straight into `BoardEngine.invalidate` and only the
 * touched spaces re-solve on their next request — a change in a never-opened
 * space costs nothing until opened.
 *
 * Granularity is the space, deliberately: it's the unit the engine caches and
 * the unit a layout re-solve operates on. A module's add/remove/edit dirties
 * the space that CONTAINS it (its parent) — siblings re-pack, the module's own
 * subspace is untouched unless its membership changed. Edge changes dirty the
 * space they aggregate into, computed via the real `prepareLayout` aggregation
 * (LCA routing included) rather than a re-derivation that could drift.
 */
import type { DependencyEdge, Module } from "../arch-model/model.js";
import { moduleKey } from "../arch-model/utils.js";
import { type LayoutStructure, type ModuleWithParent, prepareLayout } from "./layout.js";

export interface ModelInput {
  readonly modules: readonly Module[];
  readonly edges: readonly DependencyEdge[];
}

/** Reuse already-built topology instead of rebuilding it inside `diffModel`.
 *  The caller (board) builds one `LayoutStructure` per edit and keeps the prior
 *  one — passing both here lets the edge-aggregation diff skip TWO O(modules+
 *  edges) `prepareLayout` rebuilds (prev + next). Output is byte-identical to
 *  the structure-free path; omit either field to fall back to a fresh build. */
export interface DiffModelOptions {
  readonly prevStructure?: LayoutStructure | null;
  readonly nextStructure?: LayoutStructure;
}

export interface ModelDelta {
  readonly modulesAdded: readonly Module[];
  readonly modulesRemoved: readonly string[]; // moduleKeys
  readonly modulesChanged: readonly Module[]; // same key, new content
  readonly edgesAdded: readonly DependencyEdge[];
  readonly edgesRemoved: readonly DependencyEdge[];
  /** Space ids whose layout is stale — pass to `BoardEngine.invalidate`. */
  readonly dirtySpaces: readonly string[];
}

const ROOT_SPACE = "root::space";

/** Structural key of one raw edge — endpoints, target interface, owning process.
 *  Two edges with the same from→to but different processes have distinct keys,
 *  so a change in aggregation count (a process added/removed on an existing
 *  pair) shows up as a signature change. */
function rawEdgeKey(e: DependencyEdge): string {
  return `${e.fromModuleId ?? e.fromModulePath.join(".")}>${e.toModuleId ?? e.toModulePath.join(".")}|${e.toInterfacePath.join(".")}|${e.processId ?? ""}|${e.stepIndex ?? ""}`;
}

/**
 * Order-insensitive signature of an edge set. The board uses it to decide
 * whether ANY edge changed this edit: if the signature is unchanged, no edge
 * was added, removed, or re-routed, so the only invalidation the per-space
 * layout signature can't see itself (cross-level external ports, internal
 * calls, aggregation counts — all edge-derived) is impossible. A pure module
 * value edit keeps this stable → the board skips `diffModel` entirely and lets
 * each space's own content signature catch the change.
 */
export function edgesSignature(edges: readonly DependencyEdge[]): string {
  return edges.map(rawEdgeKey).sort().join("\n");
}

/** Space id that DIRECTLY CONTAINS a module with this parent key. */
function spaceOfParent(parentKey: string | undefined): string {
  return parentKey ? `${parentKey}::space` : ROOT_SPACE;
}

/** Layout-affecting content signature of a single (flattened) module. Excludes
 *  `children` (flat siblings diff on their own key) and `parentId` (a move is
 *  handled separately so both old + new parent spaces dirty). */
function moduleSig(m: ModuleWithParent): string {
  return JSON.stringify([
    m.name,
    m.typeName,
    m.baseType,
    m.description ?? null,
    m.fields,
    m.aspects,
    m.interfaces,
    m.surfaces,
  ]);
}

/** Per-parent edge multiset signature, from the real LCA aggregation. Reuses a
 *  prebuilt `LayoutStructure` when given (the edges/ports are identical to what
 *  `prepareLayout` would route), avoiding a redundant O(modules+edges) build. */
function edgeSpaceSigs(input: ModelInput, structure?: LayoutStructure | null): Map<string, string> {
  const edgesByParent = structure
    ? structure.edgesByParent
    : prepareLayout(input.modules, input.edges).ctx.edgesByParent;
  const out = new Map<string, string>();
  for (const [parentKey, edges] of edgesByParent) {
    const sig = edges
      .map((e) => `${e.from}>${e.to}#${e.details.length}`)
      .sort()
      .join("|");
    out.set(spaceOfParent(parentKey), sig);
  }
  return out;
}

export function diffModel(
  prev: ModelInput | null,
  next: ModelInput,
  opts: DiffModelOptions = {},
): ModelDelta {
  const dirty = new Set<string>();

  const prevMods = new Map<string, ModuleWithParent>();
  if (prev) for (const m of prev.modules as readonly ModuleWithParent[]) prevMods.set(moduleKey(m), m);
  const nextMods = new Map<string, ModuleWithParent>();
  for (const m of next.modules as readonly ModuleWithParent[]) nextMods.set(moduleKey(m), m);

  const modulesAdded: Module[] = [];
  const modulesChanged: Module[] = [];
  const modulesRemoved: string[] = [];

  for (const [key, m] of nextMods) {
    const before = prevMods.get(key);
    if (!before) {
      modulesAdded.push(m);
      dirty.add(spaceOfParent(m.parentId)); // appears in its parent's space
      continue;
    }
    const moved = before.parentId !== m.parentId;
    if (moved) {
      dirty.add(spaceOfParent(before.parentId)); // left this space
      dirty.add(spaceOfParent(m.parentId)); // joined this one
    }
    if (moved || moduleSig(before) !== moduleSig(m)) {
      modulesChanged.push(m);
      dirty.add(spaceOfParent(m.parentId)); // its size/label may have changed
    }
  }
  for (const [key, m] of prevMods) {
    if (!nextMods.has(key)) {
      modulesRemoved.push(key);
      dirty.add(spaceOfParent(m.parentId)); // gone from its parent's space
    }
  }

  // Edges: diff the per-space aggregated edge sets. Any space whose edge
  // signature changed re-solves (the stress solver weights edges).
  const prevEdgeSigs = prev ? edgeSpaceSigs(prev, opts.prevStructure) : new Map<string, string>();
  const nextEdgeSigs = edgeSpaceSigs(next, opts.nextStructure);
  for (const id of new Set([...prevEdgeSigs.keys(), ...nextEdgeSigs.keys()])) {
    if (prevEdgeSigs.get(id) !== nextEdgeSigs.get(id)) dirty.add(id);
  }

  // Raw edge add/remove lists (structural key — DependencyEdge has no id).
  const edgeKey = (e: DependencyEdge): string =>
    `${e.fromModuleId ?? e.fromModulePath.join(".")}>${e.toModuleId ?? e.toModulePath.join(".")}|${e.toInterfacePath.join(".")}|${e.processId ?? ""}`;
  const prevEdges = new Map<string, DependencyEdge>();
  if (prev) for (const e of prev.edges) prevEdges.set(edgeKey(e), e);
  const nextEdges = new Map<string, DependencyEdge>();
  for (const e of next.edges) nextEdges.set(edgeKey(e), e);
  const edgesAdded: DependencyEdge[] = [];
  const edgesRemoved: DependencyEdge[] = [];
  for (const [k, e] of nextEdges) if (!prevEdges.has(k)) edgesAdded.push(e);
  for (const [k, e] of prevEdges) if (!nextEdges.has(k)) edgesRemoved.push(e);

  return {
    modulesAdded,
    modulesRemoved,
    modulesChanged,
    edgesAdded,
    edgesRemoved,
    dirtySpaces: [...dirty],
  };
}
